import { Injectable } from '@angular/core';
import { AppState } from '../models/app-state.model';
import {
  CurrentPersistedAppState,
  mapRuntimeAppStateToPersisted,
} from '../persistence';
import {
  ImportedStateValidationResult,
  validateImportedStateJson,
} from '../validators/import-state.validator';
import { mapValidationErrorsToStrings } from '../validators/validation-error.mapper';

export type ImportReviewFailureReason =
  | 'empty'
  | 'parse'
  | 'unsupported-version'
  | 'invalid-format'
  | 'migration'
  | 'validation';

export interface ImportPreflightSummary {
  detectedSchemaVersion: number;
  targetSchemaVersion: number;
  categoryCount: number;
  cardCount: number;
  requiresMigration: boolean;
  willNormalize: boolean;
}

export interface ImportReviewResult extends ImportedStateValidationResult {
  failureReason: ImportReviewFailureReason | null;
  preflightSummary: ImportPreflightSummary | null;
  userMessages: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ImportExportService {
  public createExportSnapshot(state: AppState): CurrentPersistedAppState {
    return mapRuntimeAppStateToPersisted(state);
  }

  public serializeExportSnapshot(snapshot: CurrentPersistedAppState): string {
    return JSON.stringify(snapshot, null, 2);
  }

  public createExportJson(state: AppState): string {
    return this.serializeExportSnapshot(this.createExportSnapshot(state));
  }

  public validateImportJson(jsonText: string): ImportedStateValidationResult {
    return validateImportedStateJson(jsonText);
  }

  public reviewImportJson(jsonText: string): ImportReviewResult {
    const validationResult = this.validateImportJson(jsonText);
    const preflightSummary = validationResult.isValid && validationResult.currentPersistedState
      ? this.createPreflightSummary(validationResult)
      : null;
    const failureReason = validationResult.isValid
      ? null
      : this.classifyFailureReason(validationResult);
    const userMessages = validationResult.isValid
      ? this.buildSuccessMessages(preflightSummary)
      : this.buildFailureMessages(validationResult, failureReason ?? 'validation');

    return {
      ...validationResult,
      failureReason,
      preflightSummary,
      userMessages,
    };
  }

  public createExportFileName(savedAt: string): string {
    const compactTimestamp = savedAt.replace(/[:]/g, '-');

    return `project-planner-backup-${compactTimestamp}.json`;
  }

  private createPreflightSummary(
    validationResult: ImportedStateValidationResult,
  ): ImportPreflightSummary {
    const persistedState = validationResult.currentPersistedState!;

    return {
      detectedSchemaVersion: validationResult.detectedSchemaVersion ?? persistedState.schemaVersion,
      targetSchemaVersion: persistedState.schemaVersion,
      categoryCount: persistedState.categories.length,
      cardCount: persistedState.cards.length,
      requiresMigration: validationResult.migratedFromVersion !== null,
      willNormalize: validationResult.wasNormalized,
    };
  }

  private buildSuccessMessages(preflightSummary: ImportPreflightSummary | null): string[] {
    if (!preflightSummary) {
      return ['Import preflight passed. Review the overwrite confirmation before replacing the current state.'];
    }

    return [
      'Import preflight passed. Review the overwrite confirmation before replacing the current state.',
      preflightSummary.requiresMigration
        ? `Migration required: schema v${preflightSummary.detectedSchemaVersion} will be upgraded to v${preflightSummary.targetSchemaVersion}.`
        : `Migration not required: the backup already matches schema v${preflightSummary.targetSchemaVersion}.`,
      preflightSummary.willNormalize
        ? 'Normalization repair will run before the imported state is saved.'
        : 'No normalization repair is needed for this backup.',
    ];
  }

  private buildFailureMessages(
    validationResult: ImportedStateValidationResult,
    failureReason: ImportReviewFailureReason,
  ): string[] {
    const detailedMessages = mapValidationErrorsToStrings(validationResult.errors);

    switch (failureReason) {
      case 'empty':
        return ['Import JSON cannot be empty.'];
      case 'parse':
        return ['Parse failure: The selected file could not be parsed as valid JSON.'];
      case 'unsupported-version':
        return [
          `Unsupported version: schema v${validationResult.detectedSchemaVersion ?? 'unknown'} cannot be imported by this app.`,
        ];
      case 'invalid-format':
        return [
          'Invalid backup format: The selected JSON is not a recognized Project Planning App backup.',
        ];
      case 'migration':
        return [
          'Migration failure: The backup could not be upgraded safely to the current schema.',
          ...detailedMessages,
        ];
      case 'validation':
      default:
        return [
          'Validation failure: The backup was parsed, but its contents are incomplete or inconsistent.',
          ...detailedMessages,
        ];
    }
  }

  private classifyFailureReason(
    validationResult: ImportedStateValidationResult,
  ): ImportReviewFailureReason {
    const firstError = validationResult.errors[0] ?? '';

    if (firstError === 'Import JSON cannot be empty.') {
      return 'empty';
    }

    if (firstError === 'Import file is not valid JSON.') {
      return 'parse';
    }

    if (firstError.includes('is not supported')) {
      return 'unsupported-version';
    }

    if (
      firstError.includes('missing a numeric schemaVersion')
      || firstError.includes('does not match schema version')
    ) {
      return 'invalid-format';
    }

    if (
      firstError.includes('Migration failed')
      || firstError.includes('No migrator is registered')
    ) {
      return 'migration';
    }

    return 'validation';
  }
}